import { useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, ArrowUpRight, Copy, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import "./SupportModal.css";

const SUPPORT_NAME = "Eliabe Andrade";
const SUPPORT_PHONE_DISPLAY = "(81) 99946-2923";
const SUPPORT_PHONE_INTL = "5581999462923";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

const TOPICS = [
  { id: "acesso", chip: "Não consigo entrar", subject: "Não consigo fazer login" },
  { id: "senha", chip: "Esqueci a senha", subject: "Esqueci minha senha" },
  { id: "conta", chip: "Ainda não tenho conta", subject: "Ainda não tenho acesso e quero cadastrar minha empresa" },
];

const TIPS = [
  "Confira se o e-mail está escrito certinho, sem espaços.",
  "Veja se o Caps Lock está ligado na hora de digitar a senha.",
  "Atualize a página (F5) e tente entrar de novo.",
];

// A mensagem chega identificada com o nome do sistema. A senha nunca entra aqui.
function buildSupportMessage({ subject, email, company, details, error, attemptCount }) {
  const now = new Date();
  const horario = now.toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" });
  const lines = [
    "*CobraZap • Suporte de acesso*",
    `${greeting()}, ${SUPPORT_NAME.split(" ")[0]}! Preciso de ajuda com o painel do CobraZap.`,
    "",
    `📌 Assunto: ${subject}`,
  ];
  const mail = email.trim();
  if (mail) lines.push(`📧 E-mail: ${mail}`);
  const empresa = company.trim();
  if (empresa) lines.push(`🏢 Empresa: ${empresa}`);
  const err = (error || "").trim();
  if (err) lines.push(`⚠️ Erro na tela: ${err.length > 140 ? `${err.slice(0, 137)}...` : err}`);
  if (attemptCount >= 2) lines.push(`🔁 Já tentei ${attemptCount}x sem conseguir`);
  const extra = details.trim();
  if (extra) lines.push(`💬 Detalhes: ${extra}`);
  lines.push(`🕒 ${horario}`);
  lines.push("", "Pode me ajudar? 🙏");
  return lines.join("\n");
}

async function copyText(text, okMessage) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(okMessage);
  } catch {
    toast.error("Não consegui copiar. Selecione e copie manualmente.");
  }
}

function SupportForm({ email: initialEmail, error, attemptCount }) {
  const [topicId, setTopicId] = useState(TOPICS[0].id);
  const [email, setEmail] = useState(initialEmail || "");
  const [company, setCompany] = useState("");
  const [details, setDetails] = useState("");

  const topic = TOPICS.find((t) => t.id === topicId) || TOPICS[0];
  const message = useMemo(
    () => buildSupportMessage({ subject: topic.subject, email, company, details, error, attemptCount }),
    [topic.subject, email, company, details, error, attemptCount],
  );
  const href = `https://wa.me/${SUPPORT_PHONE_INTL}?text=${encodeURIComponent(message)}`;

  const fieldClass =
    "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500 " +
    "dark:bg-slate-950 dark:border-slate-800 dark:text-white dark:placeholder:text-slate-600";
  const labelClass = "text-slate-600 dark:text-slate-400 text-xs uppercase tracking-[0.15em]";

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto p-6 space-y-5 themed-scrollbar">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3 pr-8">
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-300 to-teal-500 text-emerald-950 font-outfit font-bold flex items-center justify-center">
            EA
          </div>
          <span
            className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white dark:border-slate-900"
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0">
          <DialogTitle className="font-outfit text-lg text-slate-900 dark:text-white">Suporte CobraZap</DialogTitle>
          <DialogDescription className="text-xs mt-1 text-slate-500 dark:text-slate-400">
            Fale direto com {SUPPORT_NAME} pelo WhatsApp.
          </DialogDescription>
        </div>
      </div>

      {/* O que aconteceu */}
      {error && (
        <div
          data-testid="support-error-summary"
          className="flex items-start gap-2 rounded-md border px-3 py-2 text-sm border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] opacity-80">
              Erro ao entrar{attemptCount >= 2 ? ` · ${attemptCount}ª tentativa` : ""}
            </div>
            {error}
          </div>
        </div>
      )}

      {/* Dicas rápidas */}
      <div className="rounded-lg border p-3 border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/50">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Antes de chamar, tente</div>
        <ul className="space-y-1.5">
          {TIPS.map((t) => (
            <li key={t} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400">
              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              {t}
            </li>
          ))}
        </ul>
      </div>

      {/* Assunto */}
      <div>
        <div className={`${labelClass} mb-2`}>Assunto</div>
        <div role="group" aria-label="Assunto" className="flex flex-wrap gap-2">
          {TOPICS.map((t) => {
            const active = t.id === topicId;
            return (
              <button
                key={t.id}
                type="button"
                aria-pressed={active}
                data-testid={`support-topic-${t.id}`}
                onClick={() => setTopicId(t.id)}
                className={`h-8 px-3 rounded-full text-xs border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 ${
                  active
                    ? "bg-emerald-500/15 border-emerald-500/60 text-emerald-700 dark:text-emerald-300"
                    : "border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                }`}
              >
                {t.chip}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dados */}
      <div className="grid gap-4">
        <div className="space-y-2">
          <Label htmlFor="support-email" className={labelClass}>
            Seu e-mail de acesso
          </Label>
          <Input
            id="support-email"
            data-testid="support-email-input"
            type="email"
            autoComplete="email"
            placeholder="voce@empresa.com.br"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={fieldClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="support-company" className={labelClass}>
            Nome da empresa
          </Label>
          <Input
            id="support-company"
            data-testid="support-company-input"
            placeholder="Ex.: Minha Telecom"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className={fieldClass}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="support-details" className={labelClass}>
            Conte o que aconteceu <span className="normal-case tracking-normal text-slate-400">(opcional)</span>
          </Label>
          <Textarea
            id="support-details"
            data-testid="support-details-input"
            rows={2}
            maxLength={300}
            placeholder="Ex.: a senha não é aceita desde ontem"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            className={fieldClass}
          />
        </div>
      </div>

      {/* Prévia */}
      <div>
        <div className={`${labelClass} mb-2`}>Mensagem que será enviada</div>
        <div
          data-testid="support-message-preview"
          className="sm-bubble rounded-lg rounded-tl-none px-3 py-2.5 text-[12px] leading-relaxed whitespace-pre-line break-words border
            bg-emerald-100 text-emerald-950 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-800/60"
        >
          {message.replace(/\*/g, "")}
        </div>
      </div>
      </div>

      {/* Ações: fixas no rodapé, sempre à vista */}
      <div className="shrink-0 border-t p-4 space-y-2 border-slate-200 bg-white/80 dark:border-slate-800 dark:bg-slate-900/80">
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="login-support-btn"
          className="w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-sm font-semibold bg-[#25D366] hover:bg-[#20bd5a] text-slate-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
        >
          <MessageCircle className="w-4 h-4" />
          Chamar no WhatsApp
          <ArrowUpRight className="w-4 h-4 opacity-70" />
        </a>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="support-copy-message"
            onClick={() => copyText(message.replace(/\*/g, ""), "Mensagem copiada")}
            className="h-9 rounded-md border inline-flex items-center justify-center gap-2 text-xs transition-colors border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
          >
            <Copy className="w-3.5 h-3.5" /> Copiar mensagem
          </button>
          <button
            type="button"
            data-testid="support-copy-phone"
            onClick={() => copyText(SUPPORT_PHONE_DISPLAY, "Número copiado")}
            className="h-9 rounded-md border inline-flex items-center justify-center gap-2 text-xs transition-colors border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
          >
            <Copy className="w-3.5 h-3.5" /> {SUPPORT_PHONE_DISPLAY}
          </button>
        </div>
        <p className="text-center text-[11px] text-slate-500">
          A mensagem já vai pronta, é só enviar.
        </p>
      </div>
    </>
  );
}

export default function SupportModal({ open, onOpenChange, email, error, isDark, attemptCount = 0 }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* O modal renderiza fora da página, então o tema é aplicado aqui: `dark` no
          conteúdo e o visual num wrapper interno (descendente de `.dark`). */}
      <DialogContent
        data-testid="support-modal"
        className={`p-0 gap-0 border-0 bg-transparent shadow-none w-[calc(100%-2rem)] max-w-md sm:rounded-2xl ${
          isDark ? "dark text-slate-300" : "text-slate-600"
        }`}
      >
        <div className="flex flex-col max-h-[90vh] overflow-hidden rounded-2xl border bg-white border-slate-200 shadow-2xl dark:bg-slate-900 dark:border-slate-800">
          <SupportForm email={email} error={error} attemptCount={attemptCount} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
